import { Component, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '@core/auth/auth.service';
import { SupportedLang } from '@core/i18n/i18n.const';
import { LanguageService } from '@core/i18n/language.service';
import { Icons, KCIconComponent } from '@keepcontrol/kc-icons';
import { TranslatePipe } from '@ngx-translate/core';
import { SheetShellComponent } from '../../components/sheet-shell/sheet-shell.component';

/** Une langue proposée par le sélecteur. */
interface LangOption {
  /** Code de langue (`fr`, `en`, `de`, `es`). */
  code: SupportedLang;
  /**
   * Nom de la langue dans sa propre langue.
   *
   * @remarks
   * Un endonyme ne se traduit pas : un visiteur cherche « Deutsch » dans la liste, pas
   * « Allemand ». Ces quatre libellés sont donc les seuls textes de l'application à rester
   * identiques dans les quatre langues.
   */
  name: string;
}

/**
 * Barre haute permanente (maquette) : marque, sélecteur de langue, bandeau de session.
 */
@Component({
  selector: 'app-top-bar',
  imports: [RouterLink, KCIconComponent, SheetShellComponent, TranslatePipe],
  templateUrl: './top-bar.component.html',
  styleUrl: './top-bar.component.scss',
})
export class TopBarComponent {
  private readonly router = inject(Router);

  /** Session courante : alimente le bandeau d'identité. */
  protected readonly auth = inject(AuthService);

  /** Langue active de l'application, pilotée par le sélecteur de cette barre. */
  private readonly language = inject(LanguageService);

  /** Pictogramme du sélecteur de langue. */
  protected readonly languageIcon = Icons.Language;
  /** Pictogramme de session, affiché à défaut d'initiales. */
  protected readonly userIcon = Icons.User;

  /** Un tag peut être lu par n'importe qui : la fiche suit la langue choisie. */
  protected readonly langs: LangOption[] = [
    { code: 'fr', name: 'Français' },
    { code: 'en', name: 'English' },
    { code: 'de', name: 'Deutsch' },
    { code: 'es', name: 'Español' },
  ];

  /** Langue active. */
  protected readonly currentLang = this.language.current;

  /** Vrai quand le panneau de choix de langue est ouvert. */
  protected readonly isLangSheetOpen = signal(false);

  /** Ouvre le panneau de choix de langue. */
  protected openLangSheet(): void {
    this.isLangSheetOpen.set(true);
  }

  /** Referme le panneau de choix de langue. */
  protected closeLangSheet(): void {
    this.isLangSheetOpen.set(false);
  }

  /**
   * Applique la langue choisie et referme le panneau.
   *
   * @remarks
   * Choix **explicite** : il est mémorisé sur cet appareil et ne sera plus écrasé par la langue
   * du compte aux connexions suivantes.
   */
  protected selectLang(lang: LangOption): void {
    void this.language.use(lang.code, { manual: true });
    this.closeLangSheet();
  }

  /** Ouvre l'écran compte/connexion en mémorisant la page courante. */
  protected goToAccount(): void {
    // Déjà sur l'écran compte : ne pas s'y renvoyer, sinon le retour après connexion
    // pointerait sur cet écran au lieu de la page d'origine
    if (this.router.url.startsWith('/login')) {
      return;
    }

    this.router.navigate(['/login'], {
      queryParams: { returnUrl: this.router.url },
    });
  }
}
